import { useFrontEndEventTrackingService as frontEndEventTrackingService } from '@clickcease/cc-client-components-lib'
import { create, StateCreator } from 'zustand'
import { devtools } from 'zustand/middleware'

import { PlatformModals, Platforms, PLATFORM_MODALS } from '../models'

export interface PlatformsModalsStore {
  openModal: PlatformModals | undefined
  selectedPlatform: Platforms | undefined
  multiAccountModal: 'edit' | 'remove' | undefined
  selectedConnectionId: number | undefined
  googleAdsIdUpdated: boolean | undefined
  setSelectedConnectionId: (id: number | undefined) => void
  setMultiAccountModal: (modal: 'edit' | 'remove' | undefined) => void
  closeModal: () => void
  setOpenModal: (modal: PlatformModals | undefined) => void
  setSelectedPlatform: (platform: Platforms | undefined) => void
  setGoogleAdsIdUpdated: (value: boolean | undefined) => void
  openPlatformConnection: (platform: Platforms, isEdit?: boolean) => () => void
  openDisableModal: (platform: Platforms) => () => void
  openPixelGuardModal: (platform: Platforms) => () => void
  openAudienceModal: (platform: Platforms) => () => void
}

const platformsModalsStore = (set: (fn: () => Partial<PlatformsModalsStore>) => void) => {
  const { track } = frontEndEventTrackingService()

  return {
    openModal: undefined,
    selectedPlatform: undefined,
    googleAdsIdUpdated: undefined,
    multiAccountModal: undefined,
    selectedConnectionId: undefined,
    setSelectedConnectionId: (id: number | undefined) => set(() => ({ selectedConnectionId: id })),
    setMultiAccountModal: (modal: 'edit' | 'remove' | undefined) =>
      set(() => ({ multiAccountModal: modal })),
    closeModal: () => set(() => ({ openModal: undefined })),
    setOpenModal: (modal: PlatformModals | undefined) => set(() => ({ openModal: modal })),
    setSelectedPlatform: (platform: Platforms | undefined) =>
      set(() => ({ selectedPlatform: platform })),
    setGoogleAdsIdUpdated: (value: boolean | undefined) =>
      set(() => ({ googleAdsIdUpdated: !!value })),
    openPlatformConnection: (platform: Platforms, isEdit?: boolean) => () => {
      set(() => ({
        selectedPlatform: platform,
        openModal: isEdit ? PLATFORM_MODALS.connectionModalEdit : PLATFORM_MODALS.connectionModal,
      }))
      track(['dataLayer', 'segment'], `Add Account ${platform}`, {})
    },
    openDisableModal: (platform: Platforms) => () => {
      const capitalizePlatform = platform.charAt(0).toUpperCase() + platform.slice(1)
      set(() => ({ selectedPlatform: platform, openModal: PLATFORM_MODALS.confirmationModal }))
      track(['dataLayer', 'segment'], `${capitalizePlatform}.ClickDisablePlatform`, {})
    },
    openPixelGuardModal: (platform: Platforms) => () => {
      set(() => ({ selectedPlatform: platform, openModal: PLATFORM_MODALS.pixelGuardModal }))
      track(['dataLayer', 'segment'], 'Google.ClickedPixelGuard', {})
    },
    openAudienceModal: (platform: Platforms) => () => {
      const capitalizePlatform = platform.charAt(0).toUpperCase() + platform.slice(1)
      set(() => ({ selectedPlatform: platform, openModal: PLATFORM_MODALS.audienceModal }))
      track(['dataLayer', 'segment'], `${capitalizePlatform}.ClickedConnectAudience`, {})
    },
  }
}

export const usePlatformsModalsStore = create<PlatformsModalsStore>()(
  devtools(
    platformsModalsStore as StateCreator<
      PlatformsModalsStore,
      [['zustand/devtools', never]],
      [],
      PlatformsModalsStore
    >
  )
)

export default usePlatformsModalsStore
